import { zodResolver } from '@hookform/resolvers/zod'
import type { BackofficeAuthVerifyTwoFactorOtpData } from '@repo/sdk/gen'
import {
	backofficeAuthSendTwoFactorOtp,
	backofficeAuthVerifyTwoFactorOtp
} from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { ButtonLink } from '@repo/ui/commons/button-link'
import { Field, FieldError, FieldGroup } from '@repo/ui/field'
import {
	isCoolingDown,
	startCooldown,
	useCooldown
} from '@repo/ui/hooks/use-cooldown'
import {
	AuthCard,
	AuthCardBody,
	AuthCardHeader
} from '@repo/ui/modules/auth/components/auth-card'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { OtpInput } from '@repo/ui/modules/auth/components/otp-input'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery
} from '@tanstack/react-query'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { Mail } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import i18n, { useTranslation } from '@/lib/i18n'
import {
	adminSessionQueryOptions,
	fetchFreshSession
} from '@/lib/query-options'

const EmailOtpSchema = z.object({
	code: z
		.string()
		.length(6, { error: () => i18n.t('auth:validation.otp_length') })
})

export const Route = createFileRoute('/(auth)/two-factor/email')({
	component: AdminTwoFactorEmail
})

function AdminTwoFactorEmail() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const router = useRouter()
	const queryClient = useQueryClient()
	const { redirect: redirectTo } = Route.useSearch()

	// The layout guard ensured a pending challenge; the null case only occurs
	// for an instant after a successful verify, while the navigation away is
	// still in flight.
	const { data: session } = useSuspenseQuery(adminSessionQueryOptions())
	const mfa = session.sessionType === 'mfa_pending' ? session.mfa : null

	const form = useForm<z.infer<typeof EmailOtpSchema>>({
		resolver: zodResolver(EmailOtpSchema),
		defaultValues: { code: '' }
	})

	const sendOtp = useMutation({
		mutationFn: async () => {
			await backofficeAuthSendTwoFactorOtp({
				body: { mfaToken: mfa?.mfaToken ?? '' }
			})
		},
		onSuccess: () => {
			toast.success(t('auth:two_factor.code_sent'))
			startCooldown('two-factor-email')
		},
		onError: (error) => toast.error(formatError(error))
	})

	// Send the code on arrival; the cooldown guard keeps a reload from
	// re-sending, the ref keeps StrictMode's double effect from doing it.
	const sentRef = useRef(false)
	const sendOtpMutate = sendOtp.mutate
	useEffect(() => {
		if (sentRef.current || isCoolingDown('two-factor-email')) {
			return
		}
		sentRef.current = true
		sendOtpMutate()
	}, [sendOtpMutate])

	const verify = useMutation({
		mutationFn: (body: BackofficeAuthVerifyTwoFactorOtpData['body']) =>
			backofficeAuthVerifyTwoFactorOtp({ body }),
		onError: () => form.resetField('code'),
		onSuccess: async () => {
			await fetchFreshSession(queryClient)
			// The dashboard loads before the navigation, so it opens ready
			// instead of flashing its loader. A failed preload only costs
			// that: navigation loads the route again.
			await router
				.preloadRoute({ to: redirectTo ?? '/' })
				.catch(() => undefined)
			await navigate({ to: redirectTo ?? '/' })
		}
	})

	if (!mfa) {
		return null
	}

	return (
		<AuthCard>
			<AuthCardHeader
				description={t('auth:two_factor.email_description')}
				icon={<Mail className='size-8' />}
				title={t('auth:two_factor.title')}
			/>
			<AuthCardBody>
				<form
					onSubmit={form.handleSubmit((data) =>
						verify.mutate({
							mfaToken: mfa.mfaToken,
							code: data.code
						})
					)}
				>
					<FieldGroup>
						{verify.isError && (
							<AuthErrorAlert
								onClose={() => verify.reset()}
								title={formatError(verify.error)}
							/>
						)}
						<Controller
							control={form.control}
							name='code'
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<OtpInput
										disabled={verify.isPending}
										onChange={field.onChange}
										value={field.value}
									/>
									{fieldState.invalid && (
										<FieldError
											errors={[fieldState.error]}
										/>
									)}
								</Field>
							)}
						/>
						<ResendRow
							onResend={() => sendOtp.mutate()}
							pending={sendOtp.isPending}
						/>
						<Field>
							<Button disabled={verify.isPending} type='submit'>
								{verify.isPending ? (
									<Spinner />
								) : (
									t('auth:two_factor.verify')
								)}
							</Button>
						</Field>
					</FieldGroup>
				</form>
				<div className='flex justify-center'>
					<ButtonLink
						search={(previous) => previous}
						to='/two-factor/methods'
						variant='link'
					>
						{t('auth:two_factor.use_another_method')}
					</ButtonLink>
				</div>
			</AuthCardBody>
		</AuthCard>
	)
}

// Isolated leaf so the per-second cooldown tick re-renders only this row,
// never the card.
function ResendRow(props: { pending: boolean; onResend: () => void }) {
	const { t } = useTranslation()
	const cooldown = useCooldown('two-factor-email')

	return (
		<div className='flex items-center justify-center gap-1 text-muted-foreground text-sm'>
			<span>{t('auth:two_factor.resend_prompt')}</span>
			{cooldown.isCoolingDown && (
				<span>
					{t('auth:two_factor.resend_cooldown', {
						time: `${String(
							Math.floor(cooldown.secondsLeft / 60)
						).padStart(2, '0')}:${String(
							cooldown.secondsLeft % 60
						).padStart(2, '0')}`
					})}
				</span>
			)}
			{!cooldown.isCoolingDown && (
				<Button
					className='h-auto p-0 text-sm'
					disabled={props.pending}
					onClick={props.onResend}
					size='sm'
					type='button'
					variant='link'
				>
					{t('auth:two_factor.resend')}
				</Button>
			)}
		</div>
	)
}
